import { useApolloClient } from '@apollo/client';
import ParentFrameSupplier from '@aurora/shared-utils/analytics/ParentFrameSupplier';
import { useCallback, useContext, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import AppContext from '../AppContext/AppContext';
import TenantContext from '../TenantContext';
import useMutationWithTracing from '../../useMutationWithTracing';
import type { AnalyticsEventsInput } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  AnalyticsMutation,
  AnalyticsMutationVariables,
  EndCurrentParentFrameMutation,
  EndCurrentParentFrameMutationVariables
} from '@aurora/shared-generated/types/graphql-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import analyticsMutation from './Analytics.mutation.graphql';
import frameEndMutation from './AnalyticsFrameEnd.mutation.graphql';

/**
 * `usePageTracking` calls the Analytics Event for page view.
 *
 * @author Madhusudhana RK
 */
export default function usePageTracking(pageId: EndUserPages | string) {
  const { sameSite } = useContext(TenantContext);
  const { contextNode, contextMessage } = useContext(AppContext);
  const parentFrameId = ParentFrameSupplier.getParentFrameId();
  const client = useApolloClient();

  /** Aurora analytics mutation graphql */
  const [analytics] = useMutationWithTracing<AnalyticsMutation, AnalyticsMutationVariables>(
    module,
    analyticsMutation
  );

  const [frameEnd] = useMutationWithTracing<
    EndCurrentParentFrameMutation,
    EndCurrentParentFrameMutationVariables
  >(module, frameEndMutation, {
    variables: { uuid: parentFrameId },
    context: { parentFrameId },
    onCompleted: () => ParentFrameSupplier.setParentFrameId(uuid())
  });

  const callToAnalytics = useCallback(async (): Promise<void> => {
    // analytics mutation only works where aurora is on same host as LIA
    const updatedFrameId = ParentFrameSupplier.getParentFrameId();
    if (sameSite) {
      const eventsInput: AnalyticsEventsInput = {
        pageView: {
          name: pageId,
          url: window.location.href
        }
      };

      if (contextMessage?.id) {
        eventsInput.domainContext = { target: { id: contextMessage.id } };
        eventsInput.messages = [{ target: { id: contextMessage.id } }];
      } else if (contextNode?.id) {
        // if private community is enabled, the context node has no id for anonymous users
        eventsInput.domainContext = { target: { id: contextNode.id } };
      }

      //Call Analytics after FrameEnd is called with fresh uuid
      await analytics({
        context: {
          parentFrameId: updatedFrameId
        },
        variables: {
          eventsInput
        }
      }).finally(() => {
        ParentFrameSupplier.setParentFrameId(uuid());
        if (contextMessage?.id) {
          // The below logic needs to be triggered once user views the replies/message
          // This will evict the metrics, unread counts on messages and nodes hierarchy
          // to get latest info when user navigates back to other places.
          const { read, conversation, board } = contextMessage;
          if (!read || conversation.unreadReplyCount > 0) {
            [board, ...(board?.ancestors?.edges.map(edge => edge.node) || [])].forEach(node => {
              if (node) {
                client.cache.evict({
                  id: client.cache.identify(node),
                  fieldName: 'unreadMessagesCount'
                });
              }
            });
          }
          if (conversation.unreadReplyCount > 0) {
            client.cache.evict({
              id: client.cache.identify(conversation),
              fieldName: 'unreadReplyCount'
            });
          }
          client.cache.evict({
            id: client.cache.identify(contextMessage),
            fieldName: 'metrics'
          });
          client.cache.gc();
        }
      });
    }
  }, [sameSite, pageId, contextMessage, contextNode.id, analytics, client.cache]);

  // Execute FrameEnd if user unload the component by any means
  useEffect(() => {
    const listener = () => {
      const currentFrameId = ParentFrameSupplier.getParentFrameId();
      frameEnd({
        variables: { uuid: currentFrameId },
        context: { parentFrameId: currentFrameId }
      });
    };
    window.addEventListener('beforeunload', listener);

    return (): void => {
      window.removeEventListener('beforeunload', listener);
    };
  }, [frameEnd]);

  //Call only when new page viewed. Do nothing on tab switch and dynamic url update.
  useEffect(() => {
    callToAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
